// Gem packs in the Toy Shop: prices come from the Worker's catalog; buying needs an account and
// billing details, then leaves for the payment page.
import { ApiError } from '../platform/api';
import { friendlyError, isVerifiedUser, type Accounts } from '../platform/auth';
import { gemsForSku, type Payments } from '../platform/payments';
import { CUSTOM_GEMS, GEM_PACKS, customGems } from '../data/gemPacks';
import type { AccountUI } from './accountUi';

export class GemStore {
  private status: 'loading' | 'ready' | 'error' = 'loading';
  private buying: string | null = null;

  constructor(private payments: Payments, private accounts: Accounts, private accountUi: AccountUI,
    private notify: (text: string, color: string) => void) {
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      await this.payments.loadCatalog();
      this.status = 'ready';
    } catch (e) {
      console.warn('Gem catalog unavailable:', e);
      this.status = 'error';
    }
  }

  /** The Toy Shop's Gems tab (re-rendered by the shop's refresh loop). */
  html(): string {
    if (this.status === 'loading') return '<div class="ov-note">Loading Gem packs…</div>';
    if (this.status === 'error' || !this.payments.catalog.length) {
      return '<div class="ov-note">Gem packs can\'t be reached right now. Please try again later.</div>';
    }
    const u = this.accounts.user;
    const note = !u || !isVerifiedUser(u) ? '<p class="rt-note">Your Gems stay safe with a free Tiny Legends account.</p>' : '';
    const packs = this.payments.catalog;
    // Bonus against the smallest pack's Gems per euro; the biggest is the best value, 1,200 the favourite.
    const base = packs[0]!, rate = gemsForSku(base.sku)! / base.priceMinor;
    const cards = packs.map((p, i) => {
      const gems = gemsForSku(p.sku)!, busy = this.buying === p.sku;
      const bonus = Math.round((gems / (p.priceMinor * rate) - 1) * 100);
      const ribbon = i === packs.length - 1 ? '<span class="rt-ribbon best">Best value</span>'
        : gems === 1_200 ? '<span class="rt-ribbon popular">Most popular</span>' : '';
      const name = GEM_PACKS.find((x) => x.gems === gems)?.name;
      const art = `art/shop/shop_gems_${String(Math.min(i + 1, 10)).padStart(2, '0')}.webp`;
      return `<button class="rt-card${name ? ' premium' : ''}" data-act="gempack" data-arg="${p.sku}" ${this.buying ? 'disabled' : ''}>
        ${ribbon}${bonus >= 5 ? `<span class="rt-bonus">+${bonus}%</span>` : ''}
        <span class="rt-art"><img class="rt-tray" src="art/shop/shop_tray.webp" alt=""><img src="${art}" alt=""></span>
        <span class="rt-qty">${name ? `<em>${name}</em>` : ''}<b>${gems.toLocaleString()}</b><small>Gems</small></span>
        <span class="rt-price">${busy ? 'Opening…' : p.priceString}</span></button>`;
    }).join('');
    return `${note}<div class="rt-grid gems">${cards}</div>${this.customHtml()}
      <p class="rt-foot">Secure checkout · Test mode: no real money is charged.</p>`;
  }

  /** The custom-amount panel; also re-rendered alone while the slider moves. */
  customHtml(): string {
    const eur = this.customEur, cg = customGems(eur) ?? 0, busy = this.buying === `gems_custom_${eur}`;
    const pos = eurToSlider(eur);
    return `<div class="rt-custom">
        <div class="rt-custom-head"><b>Custom pack</b><span>Choose your amount</span></div>
        <div class="rt-slider" style="--p:${(pos / 10).toFixed(1)}%">
          <span class="rt-bubble">€${eur.toLocaleString()} → ${cg.toLocaleString()} Gems</span>
          <span class="rt-track"></span><span class="rt-fill"></span>
          <input type="range" class="cp-range" min="0" max="1000" value="${pos}" aria-label="Custom amount in euros">
          <span class="rt-ends"><span>€${CUSTOM_GEMS.minEur}</span><span>€${CUSTOM_GEMS.maxEur.toLocaleString()}</span></span>
        </div>
        <button class="rt-price rt-buy" data-act="gempack" data-arg="gems_custom_${eur}" ${this.buying ? 'disabled' : ''}>${busy ? 'Opening…' : `Buy €${eur.toLocaleString()}`}</button>
      </div>`;
  }

  /** Custom pack amount (whole euros), set by the slider. */
  private customEur = 20;

  /** The custom slider moved: snap to whole euros (the shop's refresh loop re-renders). */
  setCustomFromSlider(pos: number): void { this.customEur = sliderToEur(pos); }

  buy(sku: string): void {
    if (this.buying) return;
    const u = this.accounts.user;
    if (!u || !isVerifiedUser(u)) { this.accountUi.gate(); return; }
    void this.accountUi.requireBilling(() => void this.checkout(sku));
  }

  private async checkout(sku: string): Promise<void> {
    this.buying = sku;
    try {
      await this.payments.buy(sku); // navigates away on success
    } catch (e) {
      this.buying = null;
      const msg = e instanceof ApiError && e.code === 'spend_limit'
        ? "You've reached the monthly spending limit. It refills over time."
        : friendlyError(e);
      this.notify(msg, '#ff8a8a');
    }
  }
}

/** The slider runs on a log scale so €2-€20 is as easy to pick as €200-€2,500. */
function sliderToEur(pos: number): number {
  const { minEur: a, maxEur: b } = CUSTOM_GEMS;
  const eur = Math.round(a * Math.pow(b / a, Math.max(0, Math.min(1000, pos)) / 1000));
  // Nice steps: whole euros below 50, fives below 200, tens below 1,000, then fifties.
  const step = eur < 50 ? 1 : eur < 200 ? 5 : eur < 1000 ? 10 : 50;
  return Math.max(a, Math.min(b, Math.round(eur / step) * step));
}

function eurToSlider(eur: number): number {
  const { minEur: a, maxEur: b } = CUSTOM_GEMS;
  return Math.round((Math.log(eur / a) / Math.log(b / a)) * 1000);
}
